// ── Shared task_key reservation helper (C980) ──
//
// Books a real, collision-proof task_key server-side (POST /tasks/reserve) instead of
// letting the caller guess "next C-number" from a locally-loaded task list — the guess
// race is exactly what let two tabs/users create duplicate/colliding keys. The server
// atomically claims the number AND creates a real placeholder task row for it, so the
// reservation is visible to every other client and backstopped by the existing
// (project_id, task_key) UNIQUE constraint regardless of who else might be creating
// tasks concurrently.
//
// Every create UI follows the same lifecycle: reserve on open → readonly key field +
// submit disabled until reserved → PATCH-finalize the reserved row on submit →
// release (DELETE) on any dismiss path. Because the server guarantees a fresh key in
// one round-trip, callers never need their own retry-on-409 loop.

import { api } from '../api.js';

// Reserve a single task_key. Returns the created placeholder task (full row shape,
// same as POST /tasks's response) — its `task_key` is the booked key. `prefix` is
// optional and rarely needed (C1483): omitted, the server defaults CODING keys to this
// project's own `task_prefix` (Tipatask -> TPT, see api/src/lib/task-prefix.js) and
// HUMAN keys to `H` — callers display whatever prefix the response's `task.task_key`
// actually carries, never assume/hardcode one client-side.
export async function reserveTaskKey(projectId, { prefix, category = 'CODING', type = 'task', priority = 0, parent_id = null } = {}) {
  const body = { count: 1, category, type, priority };
  if (prefix) body.prefix = prefix;
  if (parent_id != null) body.parent_id = parent_id;
  const { tasks } = await api.post(`/projects/${projectId}/tasks/reserve`, body);
  return tasks[0];
}

// Release a reserved-but-never-finalized key. Fire-and-forget — callers use this from
// dismiss/cancel handlers where there's nothing useful to do with a failure.
export function releaseTaskKey(projectId, taskKey) {
  return api.delete(`/projects/${projectId}/tasks/${taskKey}`).catch(() => {});
}
